'use client';

import React from 'react';

import { SectionHeader, type SectionHeaderProps } from '@layout';

import Button, { type ButtonVariant } from '../buttons/Button';
import type { CardHeader, CardHeaderAs, CardProps } from './Card';

function isSectionHeaderPropsObject(value: CardHeader | undefined): value is SectionHeaderProps {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !React.isValidElement(value)
  );
}

export interface CardImageProps extends Omit<CardProps, 'children'> {
  children?: React.ReactNode;
  /** 1× image URL (full-bleed `object-fit: cover`). */
  cardImage: string;
  /** Optional 2× asset; forwarded as `<img srcSet="… 2x" />` when set. */
  cardImage2x?: string;
  /** Passed to `<img alt />`. */
  cardImageAlt?: string;
  /** Optional footer CTA label (also forwarded as accessible name when no other label exists). */
  cardButtonText?: React.ReactNode;
  /** Maps to `Button` `variant` (`btn-${variant}`). */
  cardButtonVariant?: ButtonVariant;
  /** Render the CTA as a link or a button. When `'a'`, pass **`cardButtonHref`**. */
  cardButtonAs?: 'a' | 'button';
  cardButtonHref?: string;
  cardButtonTarget?: React.HTMLAttributeAnchorTarget;
  cardButtonRel?: string;
  cardButtonIcon?: React.ReactNode;
  cardButtonIconPosition?: 'left' | 'right';
  cardButtonType?: 'button' | 'submit' | 'reset';
  cardButtonDisabled?: boolean;
}

/**
 * Full-bleed image card: no padding on the shell, gradient overlay, and all copy/CTA
 * in **`.card-image-body`** (header from **`Card`**, **`children`**, optional **`Button`**).
 */
const CardImage = React.forwardRef<HTMLDivElement, CardImageProps>((props, ref) => {
  const {
    cardImage,
    cardImage2x,
    cardImageAlt = '',
    header,
    title,
    headerClasses = '',
    headerType,
    as,
    className = '',
    children,
    cardButtonText,
    cardButtonVariant = 'ghost',
    cardButtonAs = 'button',
    cardButtonHref,
    cardButtonTarget,
    cardButtonRel,
    cardButtonIcon,
    cardButtonIconPosition = 'left',
    cardButtonType = 'button',
    cardButtonDisabled = false,
    ...rest
  } = props;

  const resolvedHeader: CardHeader | undefined = header ?? title;
  const headerAs: CardHeaderAs | undefined = as ?? headerType;
  const headerContainerClasses = `card-header${!headerAs && headerClasses ? ` ${headerClasses}` : ''}`;
  const headerUsesSectionHeader = isSectionHeaderPropsObject(resolvedHeader);

  const renderHeader = () => {
    if (!resolvedHeader) return null;
    if (isSectionHeaderPropsObject(resolvedHeader)) {
      const mergedHeaderClassName = ['card-header', headerClasses, resolvedHeader.headerClassName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return <SectionHeader {...resolvedHeader} headerClassName={mergedHeaderClassName} />;
    }
    if (headerAs && (typeof resolvedHeader === 'string' || typeof resolvedHeader === 'number')) {
      return React.createElement(
        headerAs,
        headerClasses ? { className: headerClasses } : null,
        resolvedHeader
      );
    }
    return resolvedHeader;
  };

  const hasButtonLabel =
    cardButtonText !== undefined &&
    cardButtonText !== null &&
    (typeof cardButtonText !== 'string' ||
      (typeof cardButtonText === 'string' && cardButtonText.trim() !== ''));

  const useAnchor = cardButtonAs === 'a' && Boolean(cardButtonHref);
  const srcSet = cardImage2x ? `${cardImage2x} 2x` : undefined;

  const ctaClassName = 'card-image-cta';

  return (
    <div ref={ref} className={`card-image${className ? ` ${className}` : ''}`} {...rest}>
      <div className='card-image-media'>
        {/* Native img: arbitrary URLs plus optional 2x `srcSet` (next/image cannot express that pair). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className='card-image-img'
          src={cardImage}
          srcSet={srcSet}
          alt={cardImageAlt}
          decoding='async'
        />
      </div>
      <div className='card-image-gradient' aria-hidden />
      <div className='card-image-footer'>
        <div className='card-image-body'>
          {resolvedHeader &&
            (headerUsesSectionHeader ? (
              renderHeader()
            ) : (
              <div className={headerContainerClasses}>{renderHeader()}</div>
            ))}
          {children}
          {hasButtonLabel ? (
            <div className='card-image-actions'>
              {useAnchor ? (
                <Button
                  as='a'
                  href={cardButtonHref!}
                  target={cardButtonTarget}
                  rel={cardButtonRel}
                  label={cardButtonText}
                  variant={cardButtonVariant}
                  icon={cardButtonIcon}
                  iconPosition={cardButtonIconPosition}
                  disabled={cardButtonDisabled}
                  className={ctaClassName}
                />
              ) : (
                <Button
                  as='button'
                  type={cardButtonType}
                  label={cardButtonText}
                  variant={cardButtonVariant}
                  icon={cardButtonIcon}
                  iconPosition={cardButtonIconPosition}
                  disabled={cardButtonDisabled}
                  className={ctaClassName}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

CardImage.displayName = 'CardImage';

export default CardImage;
